import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { UploadCloud, CheckCircle } from "lucide-react";
import { api, putFileWithProgress } from "../api";
import { useAuth } from "../auth";

const Upload = () => {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [kind, setKind] = useState(params.get("kind") === "music" ? "music" : "video");
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", category: "", genre: "", artist: "" });
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState("idle");
  const [error, setError] = useState("");
  const [publishedId, setPublishedId] = useState(null);

  useEffect(() => {
    if (ready && !user) navigate("/app/login");
  }, [ready, user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return setError("Choose a file first.");
    setError("");
    setStage("uploading");
    setProgress(0);
    try {
      const init = await api.post("/uploads/initiate", {
        filename: file.name,
        contentType: file.type || (kind === "video" ? "video/mp4" : "audio/mpeg"),
        size: file.size,
        purpose: "media",
        direct: true,
      });
      const fileId = init.fileId || init.id;
      if (init.mode === "mux" && init.uploadUrl) {
        await putFileWithProgress(init.uploadUrl, file, setProgress);
      } else {
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("PUT", `${process.env.REACT_APP_MCS_SERVER}/api/uploads/${fileId}/raw`);
          xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
          const token = localStorage.getItem("mcs_token");
          if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
          xhr.upload.onprogress = (ev) => {
            if (ev.lengthComputable) setProgress(Math.round((ev.loaded / ev.total) * 100));
          };
          xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
          xhr.onerror = () => reject(new Error("Upload failed"));
          xhr.send(file);
        });
      }
      await api.post(`/uploads/${fileId}/complete`, { parts: [] });
      setStage("publishing");
      const data =
        kind === "video"
          ? { title: form.title, description: form.description, category: form.category || "Other", visibility: "public", format: "video" }
          : { title: form.title, artist: form.artist || undefined, genre: form.genre || undefined, visibility: "public" };
      await api.post(`/uploads/${fileId}/publish`, { kind, data });
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const st = await api.get(`/uploads/${fileId}/status`);
        if (st.publishedId) {
          setPublishedId(st.publishedId);
          setStage("done");
          return;
        }
      }
      setStage("done");
    } catch (err) {
      setError(err.message);
      setStage("idle");
    }
  };

  const inputCls = "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60";

  if (!user) return null;

  if (stage === "done") {
    return (
      <div className="mx-auto max-w-lg py-16 text-center" data-testid="upload-success">
        <CheckCircle size={44} className="mx-auto text-emerald-400" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-white">Published</h1>
        <p className="mt-2 text-sm text-slate-400">Your {kind} is live on Michael C Streamline — on the web and in the phone app.</p>
        <div className="mt-6 flex justify-center gap-3">
          {publishedId && kind === "video" && (
            <Link to={`/app/watch/${publishedId}`} className="rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white" data-testid="view-published-link">
              Watch it
            </Link>
          )}
          <Link to="/app/profile" className="rounded-full border border-white/15 px-6 py-2.5 font-heading text-xs font-bold text-slate-200" data-testid="back-to-profile-link">
            Back to my profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-xl" data-testid="upload-page">
      <h1 className="mb-2 font-heading text-2xl font-bold tracking-tight text-white">Upload</h1>
      <p className="mb-6 text-sm text-slate-500">Uploads go straight to the same library as the phone app.</p>

      <div className="mb-6 grid grid-cols-2 rounded-full border border-white/10 bg-white/[0.03] p-1">
        {[["video", "Video"], ["music", "Music"]].map(([k, label]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`upload-kind-${k}`} className={`rounded-full py-2 font-heading text-sm font-semibold transition-colors ${kind === k ? "bg-crimson text-white" : "text-slate-400 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="space-y-4">
        <label className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.02] px-6 py-10 text-center transition-colors hover:border-crimson/50" data-testid="upload-file-drop">
          <UploadCloud size={32} className="text-slate-500" />
          <span className="text-sm text-slate-400">{file ? file.name : kind === "video" ? "Choose a video file" : "Choose an audio file"}</span>
          <input
            type="file"
            accept={kind === "video" ? "video/*" : "audio/*"}
            className="hidden"
            data-testid="upload-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>

        <input value={form.title} onChange={set("title")} placeholder="Title" required className={inputCls} data-testid="upload-title-input" />
        {kind === "video" ? (
          <>
            <textarea value={form.description} onChange={set("description")} placeholder="Description" rows={3} className={inputCls} data-testid="upload-description-input" />
            <input value={form.category} onChange={set("category")} placeholder="Category (e.g. Film, Music, Comedy)" className={inputCls} data-testid="upload-category-input" />
          </>
        ) : (
          <>
            <input value={form.artist} onChange={set("artist")} placeholder="Artist" className={inputCls} data-testid="upload-artist-input" />
            <input value={form.genre} onChange={set("genre")} placeholder="Genre (e.g. Hip-Hop, R&B)" className={inputCls} data-testid="upload-genre-input" />
          </>
        )}

        {error && <p className="text-sm text-crimson" data-testid="upload-error">{error}</p>}

        {stage !== "idle" && (
          <div>
            <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
              <div className="h-full bg-crimson transition-[width] duration-300" style={{ width: stage === "uploading" ? `${progress}%` : "100%" }} />
            </div>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-slate-500" data-testid="upload-stage">
              {stage === "uploading" ? `Uploading… ${progress}%` : "Preparing on the server…"}
            </p>
          </div>
        )}

        <button
          disabled={stage !== "idle"}
          className="w-full rounded-full bg-crimson py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-crimson-hover disabled:opacity-50"
          data-testid="upload-submit-button"
        >
          {stage === "idle" ? `Publish ${kind}` : "Working…"}
        </button>
      </form>
    </div>
  );
};

export default Upload;
